import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { MapPin, X } from "lucide-react";

const STARTER_MESSAGES = [
  {
    id: "welcome",
    role: "assistant",
    text: "Map Friend here. Ask for a local favorite, a trusted stay, or tonight's best move.",
  },
];

function buildLocalReply(message, spotContext) {
  const normalized = String(message || "").toLowerCase();
  if (spotContext?.name) {
    const place = spotContext.parish
      ? `${spotContext.name} in ${spotContext.parish}`
      : spotContext.name;
    if (normalized.includes("meet") || normalized.includes("friend")) {
      return `Perfect — ${place} is a strong meet-up spot. Share your live pin when you arrive so friends on CaribNavi can find you there.`;
    }
    if (normalized.includes("price") || normalized.includes("cost")) {
      return `I have transparent pricing loaded for ${spotContext.name}. Check the pin breakdown on the map, or ask about group size and I'll factor VAT/GCT.`;
    }
    return `Great choice — ${place}. I can help you plan timing, nearby gems, and how to meet friends at this verified pin.`;
  }
  if (normalized.includes("romantic")) {
    return "Try a sunset dinner on the west coast, then keep your evening flexible for a hidden rum bar.";
  }
  if (normalized.includes("family")) {
    return "Start with calm water and turtles, then book an easy lunch close to the beach for a smoother island day.";
  }
  return "Pick a verified pin on the map first — I'll tailor tips to that exact spot, parish, and island.";
}

export default function NaviGeneralChat({ spotContext = null, onClearSpotContext }) {
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState(STARTER_MESSAGES);

  useEffect(() => {
    const loadShoutout = async () => {
      try {
        const response = await axios.get(`${API}/navi/shoutouts/current`);
        if (response.data?.message) {
          setMessages((current) => {
            if (current.some((item) => item.id === response.data.slot_key)) {
              return current;
            }
            return [
              ...current,
              {
                id: response.data.slot_key,
                role: "system",
                text: response.data.message,
              },
            ];
          });
        }
      } catch (_error) {
        // Keep the concierge experience resilient when the shoutout feed is unavailable.
      }
    };

    loadShoutout();
  }, []);

  useEffect(() => {
    if (!spotContext?.pin_id) return;
    setMessages((current) => {
      const contextId = `ctx-${spotContext.pin_id}`;
      if (current.some((m) => m.id === contextId)) return current;
      const parishLine = spotContext.parish ? ` (${spotContext.parish})` : "";
      return [
        ...current,
        {
          id: contextId,
          role: "system",
          text: `Map context: ${spotContext.name}${parishLine} — ask me about meet-ups, pricing, or what's hot nearby.`,
        },
      ];
    });
  }, [spotContext]);

  const latestMessages = useMemo(() => messages.slice(-5), [messages]);

  const handleSend = (event) => {
    event.preventDefault();
    if (!draft.trim()) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: draft.trim(),
    };
    const responseMessage = {
      id: `assistant-${Date.now() + 1}`,
      role: "assistant",
      text: buildLocalReply(draft, spotContext),
    };

    setMessages((current) => [...current, userMessage, responseMessage]);
    setDraft("");
  };

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white shadow-xl p-6 h-full flex flex-col">
      <div className="mb-4">
        <p className="text-sm uppercase tracking-[0.18em] text-cyan-600 font-semibold">
          Map Friend
        </p>
        <h3 className="text-2xl font-serif font-bold text-slate-900">
          Live local guidance
        </h3>
      </div>

      {spotContext?.name && (
        <div className="mb-4 rounded-2xl border border-cyan-100 bg-cyan-50/80 px-4 py-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-cyan-700 font-semibold flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Active pin
            </p>
            <p className="text-sm font-semibold text-slate-900 truncate">{spotContext.name}</p>
            <p className="text-xs text-slate-600 truncate">
              {[spotContext.parish, spotContext.island].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge className="bg-white text-cyan-800 border-cyan-200">WGS84</Badge>
            {onClearSpotContext && (
              <button
                type="button"
                onClick={onClearSpotContext}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Clear map context"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3 mb-5 flex-1 overflow-y-auto max-h-[280px]">
        {latestMessages.map((message) => (
          <div
            key={message.id}
            className={`rounded-2xl px-4 py-3 text-sm ${
              message.role === "user"
                ? "bg-slate-900 text-white ml-8"
                : message.role === "system"
                  ? "bg-amber-50 text-amber-900 border border-amber-100"
                  : "bg-slate-50 text-slate-700 mr-8"
            }`}
          >
            {message.text}
          </div>
        ))}
      </div>

      <form onSubmit={handleSend} className="flex flex-col sm:flex-row gap-3 mt-auto">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={
            spotContext?.name
              ? `Ask about ${spotContext.name.split("—")[0].trim()}…`
              : "Ask for a trusted tip, hidden gem, or tonight's plan..."
          }
          className="rounded-full"
        />
        <Button type="submit" className="rounded-full px-6 shrink-0">
          Ask Navi
        </Button>
      </form>

      {!spotContext && (
        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Tap a map pin to load spot context ·{" "}
          <Link to="/discover" className="text-cyan-700 hover:underline">
            open Discovery
          </Link>
        </p>
      )}
    </div>
  );
}
